// =====================================================================
// Assessment Center — Instructor Panel
// Create/Edit Exam page — the exam builder.
//
// An exam is built from one or more sections; each section is
// homogenous (multiple_choice or essay), which is what makes a
// "mixed" exam possible — it's simply an exam with both section
// types. exam_type on save is derived from the sections, exactly as
// designed in the schema (see lib/mock-api.ts `deriveExamType`).
// =====================================================================
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Field,
  Input,
  PageHeader,
  Select,
  Spinner,
  Textarea,
} from "../../../shared/components/ui";
import QuestionPickerModal from "../components/QuestionPickerModal";
import {
  getExam,
  listCourses,
  publishExam,
  saveExamDraft,
  type ExamInput,
  type ExamSectionInput,
  type QuestionWithDetail,
} from "../lib/mock-api";
import type { Course, QuestionType } from "../../../shared/types/db";

interface BuilderSection extends Omit<ExamSectionInput, "question_ids" | "points_overrides"> {
  localId: string;
  questions: QuestionWithDetail[];
  pointsOverrides: Record<string, number | null>;
}

const localId = () => `local_${Math.random().toString(36).slice(2)}`;

export default function CreateExamPage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState<"draft" | "published" | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [courseId, setCourseId] = useState("");
  const [duration, setDuration] = useState(60);
  const [passingScore, setPassingScore] = useState<number | "">("");
  const [maxAttempts, setMaxAttempts] = useState(1);
  const [startAt, setStartAt] = useState("");
  const [endAt, setEndAt] = useState("");

  const [sections, setSections] = useState<BuilderSection[]>([]);
  const [pickerFor, setPickerFor] = useState<string | null>(null);

  useEffect(() => {
    listCourses().then((c) => {
      setCourses(c);
      setCourseId((prev) => prev || c[0]?.id || "");
    });
  }, []);

  useEffect(() => {
    if (!id) return;
    getExam(id).then((exam) => {
      if (!exam) {
        setError("Exam not found.");
        setLoading(false);
        return;
      }
      setTitle(exam.title);
      setDescription(exam.description ?? "");
      setCourseId(exam.course_id ?? "");
      setDuration(exam.duration_minutes);
      setPassingScore(exam.passing_score ?? "");
      setMaxAttempts(exam.max_attempts);
      setStartAt(exam.start_at ? exam.start_at.slice(0, 16) : "");
      setEndAt(exam.end_at ? exam.end_at.slice(0, 16) : "");
      setSections(
        exam.sections.map((s) => ({
          localId: localId(),
          id: s.id,
          title: s.title,
          section_type: s.section_type,
          instructions: s.instructions ?? "",
          time_limit_minutes: s.time_limit_minutes,
          questions: s.questions.map((eq) => ({ ...eq.question })),
          pointsOverrides: Object.fromEntries(
            s.questions.map((eq) => [eq.question_id, eq.points_override])
          ),
        }))
      );
      setLoading(false);
    });
  }, [id]);

  function addSection(type: QuestionType) {
    setSections((prev) => [
      ...prev,
      {
        localId: localId(),
        title: type === "essay" ? "Written Response" : "Multiple Choice",
        section_type: type,
        instructions: "",
        time_limit_minutes: null,
        questions: [],
        pointsOverrides: {},
      },
    ]);
  }

  function updateSection(lid: string, patch: Partial<BuilderSection>) {
    setSections((prev) => prev.map((s) => (s.localId === lid ? { ...s, ...patch } : s)));
  }

  function removeSection(lid: string) {
    setSections((prev) => prev.filter((s) => s.localId !== lid));
  }

  function addQuestionsToSection(lid: string, added: QuestionWithDetail[]) {
    setSections((prev) =>
      prev.map((s) =>
        s.localId === lid ? { ...s, questions: [...s.questions, ...added] } : s
      )
    );
  }

  function removeQuestion(lid: string, qId: string) {
    setSections((prev) =>
      prev.map((s) =>
        s.localId === lid
          ? { ...s, questions: s.questions.filter((q) => q.id !== qId) }
          : s
      )
    );
  }

  function moveQuestion(lid: string, qId: string, dir: -1 | 1) {
    setSections((prev) =>
      prev.map((s) => {
        if (s.localId !== lid) return s;
        const idx = s.questions.findIndex((q) => q.id === qId);
        const swapWith = idx + dir;
        if (idx === -1 || swapWith < 0 || swapWith >= s.questions.length) return s;
        const next = [...s.questions];
        [next[idx], next[swapWith]] = [next[swapWith], next[idx]];
        return { ...s, questions: next };
      })
    );
  }

  function setPointsOverride(lid: string, qId: string, value: string) {
    setSections((prev) =>
      prev.map((s) =>
        s.localId === lid
          ? {
              ...s,
              pointsOverrides: {
                ...s.pointsOverrides,
                [qId]: value === "" ? null : Number(value),
              },
            }
          : s
      )
    );
  }

  function buildInput(): ExamInput {
    return {
      course_id: courseId || null,
      title: title.trim(),
      description: description.trim() || undefined,
      duration_minutes: duration,
      passing_score: passingScore === "" ? null : passingScore,
      max_attempts: maxAttempts,
      start_at: startAt ? new Date(startAt).toISOString() : null,
      end_at: endAt ? new Date(endAt).toISOString() : null,
      sections: sections.map((s) => ({
        id: s.id,
        title: s.title,
        instructions: s.instructions || undefined,
        section_type: s.section_type,
        time_limit_minutes: s.time_limit_minutes,
        question_ids: s.questions.map((q) => q.id),
        points_overrides: s.pointsOverrides,
      })),
    };
  }

  function validateCommon(): string | null {
    if (!title.trim()) return "Give the exam a title.";
    if (duration <= 0) return "Duration must be greater than zero.";
    if (sections.length === 0) return "Add at least one section.";
    return null;
  }

  async function handleSave(status: "draft" | "published") {
    const commonError = validateCommon();
    if (commonError) {
      setError(commonError);
      return;
    }
    setError(null);
    setSaving(status);
    try {
      const input = buildInput();
      if (status === "draft") {
        await saveExamDraft(input, id);
      } else {
        await publishExam(input, id);
      }
      navigate("/instructor/assessment-center/exams");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't save the exam.");
    } finally {
      setSaving(null);
    }
  }

  const totalPoints = sections.reduce(
    (sum, s) =>
      sum +
      s.questions.reduce(
        (sSum, q) => sSum + (s.pointsOverrides[q.id] ?? q.default_points),
        0
      ),
    0
  );

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-20 text-slate-500">
        <Spinner /> Loading exam…
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader
        title={isEdit ? "Edit Exam" : "Create Exam"}
        subtitle="Build a multiple choice, essay, or mixed exam from your question bank."
      />

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Exam details */}
      <Card className="p-5">
        <div className="grid gap-5 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Field label="Exam title" required>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Air Law — Midterm Assessment" />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Description">
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} />
            </Field>
          </div>
          <Field label="Course">
            <Select value={courseId} onChange={(e) => setCourseId(e.target.value)}>
              <option value="">Not tied to a course</option>
              {courses.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.title}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Duration (minutes)" required>
            <Input
              type="number"
              min={1}
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
            />
          </Field>
          <Field label="Passing score (points)" hint="Leave blank if not required.">
            <Input
              type="number"
              min={0}
              value={passingScore}
              onChange={(e) => setPassingScore(e.target.value === "" ? "" : Number(e.target.value))}
            />
          </Field>
          <Field label="Max attempts">
            <Input
              type="number"
              min={1}
              value={maxAttempts}
              onChange={(e) => setMaxAttempts(Number(e.target.value))}
            />
          </Field>
          <Field label="Start date" required>
            <Input type="datetime-local" value={startAt} onChange={(e) => setStartAt(e.target.value)} />
          </Field>
          <Field label="End date" required>
            <Input type="datetime-local" value={endAt} onChange={(e) => setEndAt(e.target.value)} />
          </Field>
        </div>
      </Card>

      {/* Sections */}
      <div className="mt-6 space-y-5">
        {sections.map((s) => (
          <Card key={s.localId}>
            <CardHeader
              title={
                <div className="flex items-center gap-2">
                  <input
                    value={s.title}
                    onChange={(e) => updateSection(s.localId, { title: e.target.value })}
                    className="rounded-md border border-transparent bg-transparent font-display text-lg font-semibold text-navy-900 focus:border-border-soft focus:bg-white focus:px-2 focus:py-0.5 focus:outline-none"
                  />
                  <Badge tone="navy">
                    {s.section_type === "essay" ? "Essay" : "Multiple Choice"}
                  </Badge>
                </div>
              }
              action={
                <Button
                  variant="ghost"
                  className="px-2 py-1 text-red-600 hover:bg-red-50"
                  onClick={() => removeSection(s.localId)}
                >
                  Remove section
                </Button>
              }
            />
            <div className="space-y-4 p-5">
              <Field label="Section instructions">
                <Textarea
                  value={s.instructions ?? ""}
                  onChange={(e) => updateSection(s.localId, { instructions: e.target.value })}
                  placeholder="Instructions shown to students at the start of this section…"
                />
              </Field>

              {s.questions.length === 0 ? (
                <p className="rounded-lg border border-dashed border-border-soft bg-navy-50/40 px-4 py-6 text-center text-sm text-slate-500">
                  No questions yet.
                </p>
              ) : (
                <ul className="divide-y divide-border-soft rounded-lg border border-border-soft">
                  {s.questions.map((q, i) => (
                    <li key={q.id} className="flex items-center gap-3 px-3 py-2.5">
                      <div className="flex flex-col text-slate-400">
                        <button
                          type="button"
                          disabled={i === 0}
                          onClick={() => moveQuestion(s.localId, q.id, -1)}
                          className="disabled:opacity-20"
                          aria-label="Move up"
                        >
                          ▲
                        </button>
                        <button
                          type="button"
                          disabled={i === s.questions.length - 1}
                          onClick={() => moveQuestion(s.localId, q.id, 1)}
                          className="disabled:opacity-20"
                          aria-label="Move down"
                        >
                          ▼
                        </button>
                      </div>
                      <span className="flex-1 text-sm text-slate-700">{q.prompt}</span>
                      <Input
                        type="number"
                        min={0.5}
                        step={0.5}
                        value={s.pointsOverrides[q.id] ?? q.default_points}
                        onChange={(e) => setPointsOverride(s.localId, q.id, e.target.value)}
                        className="w-20 text-center"
                        aria-label="Points for this question"
                      />
                      <button
                        type="button"
                        onClick={() => removeQuestion(s.localId, q.id)}
                        className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"
                        aria-label="Remove question"
                      >
                        ✕
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              <Button variant="secondary" onClick={() => setPickerFor(s.localId)}>
                + Add questions from bank
              </Button>
            </div>
          </Card>
        ))}

        <div className="flex gap-3">
          <Button variant="secondary" onClick={() => addSection("multiple_choice")}>
            + Multiple Choice section
          </Button>
          <Button variant="secondary" onClick={() => addSection("essay")}>
            + Essay section
          </Button>
        </div>
      </div>

      <div className="mt-6 flex items-center justify-between rounded-card border border-border-soft bg-white px-5 py-4 shadow-soft">
        <p className="text-sm text-slate-500">
          {sections.length} section{sections.length === 1 ? "" : "s"} ·{" "}
          {sections.reduce((n, s) => n + s.questions.length, 0)} question
          {sections.reduce((n, s) => n + s.questions.length, 0) === 1 ? "" : "s"} ·{" "}
          <span className="font-medium text-navy-900">{totalPoints} pts total</span>
        </p>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            onClick={() => navigate("/instructor/assessment-center/exams")}
          >
            Cancel
          </Button>
          <Button variant="secondary" disabled={!!saving} onClick={() => handleSave("draft")}>
            {saving === "draft" ? <Spinner /> : null} Save Draft
          </Button>
          <Button disabled={!!saving} onClick={() => handleSave("published")}>
            {saving === "published" ? <Spinner /> : null} Publish Exam
          </Button>
        </div>
      </div>

      {pickerFor && (
        <QuestionPickerModal
          open={!!pickerFor}
          sectionType={sections.find((s) => s.localId === pickerFor)!.section_type}
          alreadySelectedIds={
            sections.find((s) => s.localId === pickerFor)!.questions.map((q) => q.id)
          }
          onClose={() => setPickerFor(null)}
          onAdd={(qs) => addQuestionsToSection(pickerFor, qs)}
        />
      )}
    </div>
  );
}
